import React, { useEffect, useRef, useState } from 'react';
import ContentEditable from '../utils/SafeContentEditable';
import { BlockRenderer } from '../renderer';
import { createBlock } from '../utils/blockUtils';
import { focusEditable } from '../utils/focusUtils';
import {
  isCaretAtEnd,
  isCaretAtStart,
  isCaretOnFirstLine,
  isCaretOnLastLine,
  stripHtml,
} from '../utils/caretUtils';
import { getNumberedListIndex } from '../utils/indentUtils';
import { separateNextUndoStep } from '../utils/undoSeparation';

/**
 * ToggleBlock — a collapsible container (ported from the Auddict "envelope"
 * block, restyled as a Notion-style toggle). The title lives in
 * `block.content`; children are normal blocks rendered through BlockRenderer.
 *
 * Interactions:
 *  - Chevron click (or click on the header outside the title text) opens /
 *    collapses the body.
 *  - Enter in an OPEN title splits at the caret: text after the caret becomes
 *    the first child block, and the toggle stays open.
 *  - Enter in a COLLAPSED title inserts a sibling after the toggle (does not
 *    open or rewrite children — ContentEditable can hold a stale onKeyDown).
 *  - Backspace in an EMPTY title with no children converts back to a plain
 *    paragraph.
 *  - Arrow keys walk between the title, body children, and neighbouring blocks
 *    (same as other editables).
 */
export const ToggleBlock = ({
  block,
  onUpdate,
  onDelete,
  onInsertAfter,
  onNavigate,
  ...passThrough
}) => {
  const isOpen = block.props?.isOpen ?? true;
  const isEmpty = !block.children || block.children.length === 0;

  const [title, setTitle] = useState(block.content || '');
  const titleRef = useRef(null);
  const titleValueRef = useRef(title);
  // react-contenteditable skips re-render when title HTML is unchanged, so
  // onKeyDown can close over a stale `block` (wrong children / isOpen). Always
  // read the live block through this ref.
  const blockRef = useRef(block);
  blockRef.current = block;

  // Sync external tree changes into the local title (own keystrokes
  // round-trip equal — same staleness guard pattern as TextBlock)
  useEffect(() => {
    if ((block.content || '') !== titleValueRef.current) {
      titleValueRef.current = block.content || '';
      setTitle(block.content || '');
    }
  }, [block.content]);

  const setOpen = (open) => {
    const live = blockRef.current;
    onUpdate(live.id, { props: { ...live.props, isOpen: open } });
  };

  const handleHeaderClick = (e) => {
    // Title text handles its own clicks (caret placement)
    if (titleRef.current && titleRef.current.contains(e.target)) return;
    e.stopPropagation();
    setOpen(!(blockRef.current.props?.isOpen ?? true));
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    titleValueRef.current = value;
    setTitle(value);
    onUpdate(blockRef.current.id, { content: value });
  };

  const focusFirstChild = (childId) => {
    setTimeout(() => focusEditable(childId, { atStart: true }), 0);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const live = blockRef.current;
      let before = titleValueRef.current;
      let after = '';
      const selection = window.getSelection();
      if (selection.rangeCount > 0 && titleRef.current) {
        const range = selection.getRangeAt(0);
        const afterRange = range.cloneRange();
        afterRange.selectNodeContents(titleRef.current);
        afterRange.setStart(range.endContainer, range.endOffset);
        after = afterRange.toString();
        const beforeRange = range.cloneRange();
        beforeRange.selectNodeContents(titleRef.current);
        beforeRange.setEnd(range.startContainer, range.startOffset);
        before = beforeRange.toString();
      }

      titleValueRef.current = before;
      setTitle(before);

      const open = live.props?.isOpen ?? true;
      if (!open) {
        // Collapsed: new block AFTER the toggle. Never rewrite children —
        // a stale closure here used to wipe nested content.
        if (before !== (live.content || '')) {
          onUpdate(live.id, { content: before });
        }
        onInsertAfter?.(live.id, createBlock('text', after));
        return;
      }

      // Open: split title into a new first child and keep the toggle open.
      const child = createBlock('text', after);
      onUpdate(live.id, {
        content: before,
        props: { ...live.props, isOpen: true },
        children: [child, ...(live.children || [])],
      });
      focusFirstChild(child.id);
      return;
    }

    if (e.key === 'Backspace') {
      const live = blockRef.current;
      const empty = stripHtml(titleValueRef.current).trim() === '';
      const noChildren = !live.children || live.children.length === 0;
      if (empty && noChildren) {
        // Empty toggle: turn back into a plain paragraph
        e.preventDefault();
        onUpdate(live.id, { type: 'text', content: '', props: {} });
        focusEditable(live.id);
      }
      return;
    }

    // Arrow navigation: title ↔ body / neighbouring blocks
    const el = titleRef.current;
    const id = blockRef.current.id;
    if (e.key === 'ArrowUp' && el && isCaretOnFirstLine(el)) {
      if (onNavigate?.(id, 'up')) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown' && el && isCaretOnLastLine(el)) {
      if (onNavigate?.(id, 'down')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowLeft' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtStart(el)
    ) {
      if (onNavigate?.(id, 'left')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowRight' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtEnd(el)
    ) {
      if (onNavigate?.(id, 'right')) e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Single-line title — take just the first line as plain text
    const text = e.clipboardData.getData('text/plain');
    separateNextUndoStep();
    document.execCommand('insertText', false, text.split('\n')[0]);
    e.currentTarget?.dispatchEvent(
      new InputEvent('input', { bubbles: true, inputType: 'insertFromPaste', data: null })
    );
  };

  const addFirstChild = (e) => {
    e.stopPropagation();
    const live = blockRef.current;
    const child = createBlock('text');
    onUpdate(live.id, {
      props: { ...live.props, isOpen: true },
      children: [child, ...(live.children || [])],
    });
    focusFirstChild(child.id);
  };

  return (
    <div className={`toggle-block ${isOpen ? 'open' : 'closed'}`}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="toggle-header" onClick={handleHeaderClick}>
        <button
          type="button"
          className={`toggle-chevron ${isOpen ? 'toggle-chevron--open' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!(blockRef.current.props?.isOpen ?? true));
          }}
          title={isOpen ? 'Collapse' : 'Expand'}
          aria-expanded={isOpen}
        >
          <span className="toggle-chevron-icon" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M4 2.5 L9 6 L4 9.5 Z" />
            </svg>
          </span>
        </button>
        <ContentEditable
          innerRef={titleRef}
          html={title}
          onChange={handleTitleChange}
          onKeyDown={handleTitleKeyDown}
          onPaste={handlePaste}
          className="toggle-title doc-editable"
          tagName="div"
          placeholder="Toggle title…"
        />
      </div>

      {isOpen && (
        <div className="toggle-content">
          {isEmpty ? (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <div className="toggle-empty-hint" onClick={addFirstChild}>
              Empty toggle. Click to add content.
            </div>
          ) : (
            block.children.map((child, i) => (
              <BlockRenderer
                key={child.id}
                {...passThrough}
                block={child}
                onUpdate={onUpdate}
                onDelete={onDelete}
                // Destructured for the title handler — re-pass so children keep them
                onInsertAfter={onInsertAfter}
                onNavigate={onNavigate}
                prevType={block.children[i - 1]?.type}
                nextType={block.children[i + 1]?.type}
                listNumber={
                  child.type === 'number'
                    ? getNumberedListIndex(block.children, i)
                    : undefined
                }
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};
